import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { LeadFormSection } from "@/components/LeadFormSection";
import { SEO } from "@/components/SEO";
import { NavLink } from "@/components/NavLink";
import { GoogleReviewBadge } from "@/components/GoogleReviewBadge";
import { TestimonialCards } from "@/components/TestimonialCards";
import { PricingSection } from "@/components/PricingSection";
import { Helmet } from "react-helmet-async";
import { CheckCircle, Clock, Target, Users, TrendingUp, Star, ArrowRight, BookOpen, Wrench, FileText, BarChart3 } from "lucide-react";
const Home = () => {
  // LocalBusiness schema for national service
  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    "@id": "https://www.leafrushmarketing.com/#localbusiness",
    "name": "Leaf Rush Marketing",
    "url": "https://www.leafrushmarketing.com",
    "logo": "https://www.leafrushmarketing.com/og-image.jpg",
    "description": "Done-for-you website design for lawn care and landscaping companies. Professional websites starting at $200/month.",
    "areaServed": [
      { "@type": "Country", "name": "United States" },
      { "@type": "Country", "name": "Canada" }
    ],
    "serviceType": ["Website Design", "Web Development", "Local SEO"],
    "priceRange": "$200-$500/month",
    "contactPoint": {
      "@type": "ContactPoint",
      "contactType": "sales",
      "availableLanguage": "English"
    }
  };

  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": "https://www.leafrushmarketing.com/#organization",
    "name": "Leaf Rush Marketing",
    "url": "https://www.leafrushmarketing.com",
    "logo": "https://www.leafrushmarketing.com/og-image.jpg",
    "description": "Done-for-you websites for lawn care and landscaping companies that drive calls, quotes, and recurring clients."
  };

  return (
    <>
      <SEO 
        title="Lawn Care & Landscaping Websites That Get Clients"
        description="Done-for-you lawn care & landscaping website design that gets you more calls, quotes, and recurring clients—not just a pretty site. Book a free strategy call."
        keywords="lawn care website design, landscaping website design, lawn care websites, landscaping websites, websites for lawn care and landscaping businesses"
        canonical="https://www.leafrushmarketing.com/"
        ogUrl="https://www.leafrushmarketing.com/"
      />
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(localBusinessSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(organizationSchema)}
        </script>
      </Helmet>
      <div className="flex flex-col">
      {/* Hero Section */}
      <section className="relative py-20 md:py-32 overflow-hidden">
        <div className="container px-4 md:px-6 relative z-10">
          <div className="mx-auto max-w-3xl text-center space-y-8">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight">
              Lawn Care & Landscaping Website Design That Gets Clients
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              We specialize in lawn care web design and landscaping web design that gets clients using a niche, proven process that turns visitors into calls, quotes, and recurring jobs.
            </p>
            <div className="flex flex-wrap gap-6 justify-center items-center text-sm font-medium max-w-2xl mx-auto">
              <div className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-primary" />
                <span>Completely free creation</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-primary" />
                <span>Fast, proven process</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-primary" />
                <span>Built to actually get you clients</span>
              </div>
            </div>
            
            {/* Google Review Badge */}
            <div className="flex justify-center pt-2">
              <GoogleReviewBadge rating={5.0} />
            </div>
            
            <div className="pt-4 space-y-4">
              <a href="#get-started">
                <Button size="lg" className="text-lg px-8">
                  Get Started
                </Button>
              </a>
              <p className="text-sm text-muted-foreground">
                <NavLink to="/lawn-care-website-design/" className="text-primary hover:underline">
                  Lawn care website design
                </NavLink>{" "}
                |{" "}
                <NavLink to="/landscaping-website-design/" className="text-primary hover:underline">
                  Landscaping website design
                </NavLink>{" "}
                |{" "}
                <NavLink to="/websites/" className="text-primary hover:underline">
                  Pricing →
                </NavLink>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Strip */}
      <section className="py-8 bg-accent/20 border-y">
        <div className="container px-4 md:px-6">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6 text-center">
            <div className="flex flex-col items-center gap-2">
              <Target className="h-6 w-6 text-primary" />
              <p className="text-sm font-medium">Niche-Only Focus</p>
            </div>
            <div className="flex flex-col items-center gap-2">
              <Clock className="h-6 w-6 text-primary" />
              <p className="text-sm font-medium">5+ Years Experience</p>
            </div>
            <div className="flex flex-col items-center gap-2">
              <CheckCircle className="h-6 w-6 text-primary" />
              <p className="text-sm font-medium">Dozens of Sites Built</p>
            </div>
            <div className="flex flex-col items-center gap-2">
              <TrendingUp className="h-6 w-6 text-primary" />
              <p className="text-sm font-medium">10–14 Day Launch</p>
            </div>
            <div className="flex flex-col items-center gap-2">
              <Star className="h-6 w-6 text-primary" />
              <p className="text-sm font-medium">95% Retention Rate</p>
            </div>
            <div className="flex flex-col items-center gap-2">
              <Clock className="h-6 w-6 text-primary" />
              <p className="text-sm font-medium">24-Hour Response Time</p>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section 1 - Win Jobs */}
      <section className="py-20">
        <div className="container px-4 md:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">A website that helps you win jobs</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Your lawn care website design shouldn't just "look nice." It should make customers trust you, understand your services fast, and actually reach out for a quote.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
            <Card>
              <CardHeader>
                <CheckCircle className="h-10 w-10 text-primary mb-4" />
                <p className="font-semibold text-lg">Look legit instantly</p>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  Professional design that builds trust fast
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <Target className="h-10 w-10 text-primary mb-4" />
                <p className="font-semibold text-lg">Get found locally</p>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  Local SEO structure for your real service area
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <TrendingUp className="h-10 w-10 text-primary mb-4" />
                <p className="font-semibold text-lg">Convert more leads</p>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  Clear service pages + proof + simple contact flow
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <Users className="h-10 w-10 text-primary mb-4" />
                <p className="font-semibold text-lg">Grow with you</p>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  Add services/areas over time without rebuilding
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Benefits Section 2 - How Customers Buy */}
      <section className="py-20 bg-accent/20">
        <div className="container px-4 md:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Built for how lawn care & landscaping customers actually buy</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              People want to know what you do, what it costs, and whether they can trust you — quickly. We structure your landscaping website design around that decision process.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div className="flex gap-4">
              <Target className="h-8 w-8 text-primary flex-shrink-0" />
              <div>
                <p className="font-semibold mb-2">Service pages that make your offer obvious</p>
                <p className="text-sm text-muted-foreground">
                  Clear, concise pages for each service you offer
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <CheckCircle className="h-8 w-8 text-primary flex-shrink-0" />
              <div>
                <p className="font-semibold mb-2">Photos + reviews placed where they build confidence</p>
                <p className="text-sm text-muted-foreground">
                  Strategic placement of social proof throughout your site
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <Users className="h-8 w-8 text-primary flex-shrink-0" />
              <div>
                <p className="font-semibold mb-2">Easy contact path that drives real inquiries</p>
                <p className="text-sm text-muted-foreground">
                  Simple, obvious ways for customers to reach you
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <TrendingUp className="h-8 w-8 text-primary flex-shrink-0" />
              <div>
                <p className="font-semibold mb-2">Clean, no-fluff layout tailored to your niche</p>
                <p className="text-sm text-muted-foreground">
                  Focused on what lawn care & landscaping customers actually care about
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section 3 - Low Risk */}
      <section className="py-20">
        <div className="container px-4 md:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Low-risk, because we're confident in the result</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              We don't charge a build fee upfront. We build your lawn care website design first at $0 upfront, show it to you, refine it with your feedback, and you only start your monthly subscription once you're happy and ready to launch.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <Card>
              <CardHeader>
                <CheckCircle className="h-10 w-10 text-primary mb-4" />
                <p className="font-semibold text-lg">No upfront cost</p>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  No setup or build fee
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <Target className="h-10 w-10 text-primary mb-4" />
                <p className="font-semibold text-lg">You approve first</p>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  You see the site before paying anything
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <Users className="h-10 w-10 text-primary mb-4" />
                <p className="font-semibold text-lg">Month-to-month</p>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  We earn your business long-term
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Benefits Section 4 - Full Service */}
      <section className="py-20 bg-accent/20">
        <div className="container px-4 md:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">We take care of everything for you</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Most lawn care & landscaping owners don't want to mess with tech, hosting, or updates — you just want a website design that gets clients. We handle the build, maintenance, edits, and improvements so your site stays sharp while you focus on the jobs.
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-6 max-w-5xl mx-auto">
            <div className="flex flex-col items-center text-center gap-3">
              <Clock className="h-10 w-10 text-primary" />
              <p className="font-semibold">Hosting + maintenance included</p>
              <p className="text-sm text-muted-foreground">
                No need to worry about servers, security, or technical issues. We handle it all.
              </p>
            </div>

            <div className="flex flex-col items-center text-center gap-3">
              <CheckCircle className="h-10 w-10 text-primary" />
              <p className="font-semibold">Edits and updates when you need them</p>
              <p className="text-sm text-muted-foreground">
                Need to add a service? Update photos? Just let us know and we'll make it happen.
              </p>
            </div>

            <div className="flex flex-col items-center text-center gap-3">
              <TrendingUp className="h-10 w-10 text-primary" />
              <p className="font-semibold">Mobile-first and fast</p>
              <p className="text-sm text-muted-foreground">
                Your site works perfectly on all devices and loads quickly for better conversions.
              </p>
            </div>

            <div className="flex flex-col items-center text-center gap-3">
              <Target className="h-10 w-10 text-primary" />
              <p className="font-semibold">Support from someone who knows your industry</p>
              <p className="text-sm text-muted-foreground">
                Work directly with people who understand lawn care. No call centers or confusing jargon.
              </p>
            </div>
          </div>
        </div>
      </section>


      {/* How It Works */}
      <section className="py-20 bg-accent/20">
        <div className="container px-4 md:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">How It Works</h2>
            <p className="text-xl text-muted-foreground">Simple, fast, and transparent process for your lawn care website design</p>
          </div>

          <div className="grid md:grid-cols-4 gap-8 max-w-5xl mx-auto">
            <div className="text-center space-y-4">
              <div className="w-12 h-12 bg-primary text-primary-foreground rounded-full flex items-center justify-center mx-auto text-xl font-bold">
                1
              </div>
              <p className="font-semibold text-lg">Discovery Call</p>
              <p className="text-muted-foreground text-sm">
                We learn about your lawn care or landscaping business, services, and target customers
              </p>
            </div>

            <div className="text-center space-y-4">
              <div className="w-12 h-12 bg-primary text-primary-foreground rounded-full flex items-center justify-center mx-auto text-xl font-bold">
                2
              </div>
              <p className="font-semibold text-lg">Build Phase</p>
              <p className="text-muted-foreground text-sm">
                We design and develop your high-converting website
              </p>
            </div>

            <div className="text-center space-y-4">
              <div className="w-12 h-12 bg-primary text-primary-foreground rounded-full flex items-center justify-center mx-auto text-xl font-bold">
                3
              </div>
              <p className="font-semibold text-lg">Review & Revise</p>
              <p className="text-muted-foreground text-sm">
                You provide feedback and we make adjustments
              </p>
            </div>

            <div className="text-center space-y-4">
              <div className="w-12 h-12 bg-primary text-primary-foreground rounded-full flex items-center justify-center mx-auto text-xl font-bold">
                4
              </div>
              <p className="font-semibold text-lg">Launch & Support</p>
              <p className="text-muted-foreground text-sm">
                Go live and receive ongoing support and guidance
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Growth Add-Ons Teaser */}
      <section className="py-20">
        <div className="container px-4 md:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Ready to Scale?</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              After your website is built, amplify your visibility and leads with our growth services
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <Card className="border-2 hover:border-primary transition-colors">
              <CardHeader>
                <CardTitle>Google Ads Management</CardTitle>
                <CardDescription>
                  Targeted ads that reach homeowners actively searching for lawn care and landscaping services
                </CardDescription>
              </CardHeader>
              <CardContent>
                <NavLink to="/growth-add-ons/google-ads">
                  <Button variant="outline" className="w-full">Learn More About Google Ads</Button>
                </NavLink>
              </CardContent>
            </Card>

            <Card className="border-2 hover:border-primary transition-colors">
              <CardHeader>
                <CardTitle>Google Business Profile Optimization</CardTitle>
                <CardDescription>
                  Dominate the local map pack and get more calls without spending on ads
                </CardDescription>
              </CardHeader>
              <CardContent>
                <NavLink to="/growth-add-ons/gbp">
                  <Button variant="outline" className="w-full">Learn More About GBP Optimization</Button>
                </NavLink>
              </CardContent>
            </Card>
          </div>

          <div className="text-center mt-8">
            <NavLink to="/growth-add-ons">
              <Button size="lg" variant="outline">View All Growth Services</Button>
            </NavLink>
          </div>
        </div>
      </section>

      {/* Testimonials Preview */}
      <section className="py-20 bg-accent/20">
        <div className="container px-4 md:px-6">
          <TestimonialCards />
        </div>
      </section>

      {/* Pricing Section - Near Bottom */}
      <PricingSection variant="compact" showSchema={true} />

      {/* FAQ Section */}
      <section className="py-20 bg-accent/20">
        <div className="container px-4 md:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Frequently Asked Questions</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Common questions about our lawn care & landscaping website design services
            </p>
          </div>

          <div className="max-w-3xl mx-auto space-y-8">
            <div className="border-b pb-6">
              <p className="font-semibold text-lg mb-3">How long does it take to launch my new website?</p>
              <p className="text-muted-foreground">
                Most lawn care websites are built and ready to launch in about two weeks (10–14 days). You'll see your site quickly, provide feedback, and we'll refine it until you're 100% satisfied before going live.
              </p>
            </div>

            <div className="border-b pb-6">
              <p className="font-semibold text-lg mb-3">What's included in the monthly subscription?</p>
              <p className="text-muted-foreground">
                Every subscription includes: 7–14 page custom website, hosting and security, maintenance and updates, mobile-first responsive design, local SEO optimization, contact forms and lead routing, photo galleries, customer review sections, and ongoing edits and support as your business grows.
              </p>
            </div>

            <div className="border-b pb-6">
              <p className="font-semibold text-lg mb-3">Do you only work with lawn care & landscaping companies?</p>
              <p className="text-muted-foreground">
                Yes. We exclusively build websites for lawn care and landscaping businesses. This niche focus means we deeply understand your industry, your customers, and what actually drives website design that gets clients for your specific business.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Popular Guides - Internal Linking for Crawl Discovery */}
      <section className="py-20">
        <div className="container px-4 md:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Popular Guides & Resources</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Free resources to help you build a better lawn care or landscaping website
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-6 max-w-6xl mx-auto">
            <NavLink to="/websites/" className="group">
              <Card className="h-full border-2 hover:border-primary transition-colors">
                <CardHeader className="pb-3">
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center mb-3">
                    <FileText className="h-5 w-5 text-primary" />
                  </div>
                  <CardTitle className="text-lg">Website Plans</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground mb-3">
                    See pricing, features, and what's included in each plan
                  </p>
                  <span className="text-sm font-medium text-primary inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                    View Plans <ArrowRight className="h-4 w-4" />
                  </span>
                </CardContent>
              </Card>
            </NavLink>

            <NavLink to="/blog/" className="group">
              <Card className="h-full border-2 hover:border-primary transition-colors">
                <CardHeader className="pb-3">
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center mb-3">
                    <BookOpen className="h-5 w-5 text-primary" />
                  </div>
                  <CardTitle className="text-lg">Blog</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground mb-3">
                    Marketing tips and strategies for lawn care businesses
                  </p>
                  <span className="text-sm font-medium text-primary inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                    Read Articles <ArrowRight className="h-4 w-4" />
                  </span>
                </CardContent>
              </Card>
            </NavLink>

            <NavLink to="/comparisons/" className="group">
              <Card className="h-full border-2 hover:border-primary transition-colors">
                <CardHeader className="pb-3">
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center mb-3">
                    <BarChart3 className="h-5 w-5 text-primary" />
                  </div>
                  <CardTitle className="text-lg">Comparisons</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground mb-3">
                    Compare website builders, agencies, and marketing options
                  </p>
                  <span className="text-sm font-medium text-primary inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                    Compare Options <ArrowRight className="h-4 w-4" />
                  </span>
                </CardContent>
              </Card>
            </NavLink>

            <NavLink to="/website-templates/" className="group">
              <Card className="h-full border-2 hover:border-primary transition-colors">
                <CardHeader className="pb-3">
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center mb-3">
                    <FileText className="h-5 w-5 text-primary" />
                  </div>
                  <CardTitle className="text-lg">Templates</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground mb-3">
                    Free homepage, service page, and quote form templates
                  </p>
                  <span className="text-sm font-medium text-primary inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                    Get Templates <ArrowRight className="h-4 w-4" />
                  </span>
                </CardContent>
              </Card>
            </NavLink>

            <NavLink to="/website-fixes/" className="group">
              <Card className="h-full border-2 hover:border-primary transition-colors">
                <CardHeader className="pb-3">
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center mb-3">
                    <Wrench className="h-5 w-5 text-primary" />
                  </div>
                  <CardTitle className="text-lg">Website Fixes</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground mb-3">
                    Fix common issues: slow sites, no leads, ranking problems
                  </p>
                  <span className="text-sm font-medium text-primary inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                    Fix Issues <ArrowRight className="h-4 w-4" />
                  </span>
                </CardContent>
              </Card>
            </NavLink>
          </div>
        </div>
      </section>

      {/* Lead Form + Calendly */}
      <LeadFormSection />
      </div>
    </>
  );
};

export default Home;